import { inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import { submissionListTokenizer } from '../store/tokens/submission-list.token';
import { CONFIGURE_DEFAULT_PARAMS, LoadGridPayload } from '@data-access/shared';
import {
  SubmissionGridTypes,
  SMEQCListParams,
} from '../types/submission-list.types';
import { AuthService } from '@auth/auth.service';
import { User } from '@iris-sites/interfaces/sites.interfaces';

const DEFAULT_GRID_TYPE: SubmissionGridTypes = 'home';

export const submissionsListResolver = async (
  route: ActivatedRouteSnapshot,
) => {
  const submissionListSignalStore = submissionListTokenizer();
  const wcgTableUtils = inject(WcgTableUtilsService);
  const authService = inject(AuthService);

  const params = wcgTableUtils.generateTableQueryParams(
    CONFIGURE_DEFAULT_PARAMS(route),
  );

  const payload: LoadGridPayload = {
    params,
  };

  // Determine entityId based on route data (study or site)
  const entityId = route.data?.study?.id ?? route.data?.site?.id ?? undefined;

  const gridType =
    (route.data?.type as SubmissionGridTypes) ?? DEFAULT_GRID_TYPE;

  submissionListSignalStore.setByURL(gridType, entityId);
  submissionListSignalStore.loadSubmissionsGrid(payload);

  // Resolve internal user flag from observable
  const isInternal = await firstValueFrom(
    authService.user$.pipe(
      filter((user: User | undefined): user is User => !!user),
      map((user) => !!user.is_internal),
      take(1),
    ),
  );

  // If grid type is 'home' and user is internal, load SME and QC lists
  if (gridType === 'home' && isInternal) {
    const smeQCParams: SMEQCListParams = {
      attribute: 'sme',
    };

    submissionListSignalStore.loadSMEList(smeQCParams);

    submissionListSignalStore.loadQCList({
      ...smeQCParams,
      attribute: 'qc',
    });
  }

  return !!submissionListSignalStore.$submissions();
};
